import { Form, FormInstance, Input, Button } from "antd";
import { useEffect } from "react";
import {
  EditOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import TextArea from "antd/es/input/TextArea";

interface WhatSetsUsApartProps {
  heading?: string;
  subtext?: string;
  text: string[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  form: FormInstance;
  whatSetsUsApart?: WhatSetsUsApartProps;
  loading?: boolean;
}

const WhatSetsUsApart: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  whatSetsUsApart,
  loading,
}) => {
  useEffect(() => {
    if (whatSetsUsApart) {
      form.setFieldsValue({
        whatSetsUsApart: {
          heading: whatSetsUsApart.heading || "",
          subtext: whatSetsUsApart.subtext || "",
          text: whatSetsUsApart.text || [],
        },
      });
    } else {
      form.setFieldsValue({
        whatSetsUsApart: {
          heading: "",
          subtext: "",
          text: [],
        },
      });
    }
  }, [whatSetsUsApart, form]);

  return (
    <div>
      {!isEditing ? (
        <>
          <div className="hidden sm:block rounded-[20px] overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-left">Points</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {whatSetsUsApart ? (
                  <tr className="bg-[#F9F9F9] border-b">
                    <td className="py-3 px-4">
                      {whatSetsUsApart.heading || "—"}
                    </td>
                    <td className="py-3 px-4">
                      {whatSetsUsApart.subtext || "—"}
                    </td>
                    <td className="py-3 px-4">
                      {whatSetsUsApart?.text?.length > 0 ? (
                        <span>
                          {whatSetsUsApart.text[0]}
                          {whatSetsUsApart.text.length > 1 && " ..."}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="py-3 px-4 text-right text-primary">
                      <EditOutlined
                        onClick={() => setIsEditing(true)}
                        className="cursor-pointer text-[20px]"
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-3">
            {whatSetsUsApart ? (
              <div className="bg-[#F9F9F9] border rounded-lg p-4 space-y-2">
                <p>
                  <b className="text-primary">Heading:</b> 
                  <div>{whatSetsUsApart.heading}</div>
                </p>
                <p>
                  <b className="text-primary">Subtext:</b> 
                  <div>{whatSetsUsApart.subtext}</div>
                </p>

                <div>
                  <b className="text-primary">Points:</b>
                  <ul className="list-disc pl-5 mt-1">
                    {whatSetsUsApart.text.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>

                <EditOutlined
                  onClick={() => setIsEditing(true)}
                  className="cursor-pointer text-[20px] text-primary"
                />
              </div>
            ) : (
              <div className="text-center py-6">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div>
          <div className="bg-[#F9F9F9] p-4 rounded-lg">
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Heading"
                name={["whatSetsUsApart", "heading"]}
                rules={[{ required: true, message: "enter heading..." }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Subtext"
                name={["whatSetsUsApart", "subtext"]}
                rules={[{ required: true, message: "enter subtext..." }]}
              >
                <TextArea rows={3} />
              </Form.Item>

              <Form.List name={["whatSetsUsApart", "text"]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }) => (
                      <div key={key} className="flex gap-2 items-center mb-2">
                        <Form.Item
                          name={name}
                          rules={[{ required: true, message: "Enter point" }]}
                          className="flex-1 mb-0"
                        >
                          <Input placeholder="enter point..." />
                        </Form.Item>
                        <MinusCircleOutlined
                          className="text-red-500 text-lg"
                          onClick={() => remove(name)}
                        />
                      </div>
                    ))}

                    <Button
                      type="dashed"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                      block
                    >
                      Add Point
                    </Button>
                  </>
                )}
              </Form.List>
            </Form>
          </div>

          <div className="flex gap-3 mt-6">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatSetsUsApart;
